"use client";

import { useMemo } from "react";
import { useWindowStore } from "./windowStore";
import DraggableWindow from "./DraggableWindow";
import type { WindowRecord } from "./windowTypes";
import dynamic from "next/dynamic";

const Calculator = dynamic(() => import("../calculator/Calculator"), {
  ssr: false,
});
const PeriodicTable = dynamic(() => import("../periodic/PeriodicTable"), {
  ssr: false,
});
const Ruler = dynamic(() => import("../ruler/Ruler"), { ssr: false });
const Protractor = dynamic(() => import("../protractor/Protractor"), {
  ssr: false,
});
const EquationEditor = dynamic(() => import("../equation/EquationEditor"), {
  ssr: false,
});
const Todo = dynamic(() => import("../todo/Todo"), { ssr: false });
const Magnifier = dynamic(() => import("../magnifier/Magnifier"), {
  ssr: false,
});
const TTSReader = dynamic(() => import("../tts/TTSReader"), {
  ssr: false,
});
const GeoGebra = dynamic(() => import("../geogebra/GeoGebra"), {
  ssr: false,
});
const Dictionary = dynamic(() => import("../dictionary/Dictionary"), {
  ssr: false,
});
const Context7 = dynamic(() => import("../context7/Context7"), {
  ssr: false,
});
const Browser = dynamic(() => import("../browser/Browser"), {
  ssr: false,
});
const AskAI = dynamic(() => import("../askai/AskAI"), {
  ssr: false,
});
const SetSquare = dynamic(() => import("../setsquare/SetSquare"), {
  ssr: false,
});
const Annotator = dynamic(() => import("../annotator/Annotator"), {
  ssr: false,
});

function WindowContent({ win }: { win: WindowRecord }) {
  switch (win.type) {
    case "calculator":
      return <Calculator />;
    case "periodic":
      return <PeriodicTable />;
    case "ruler":
      return <Ruler windowId={win.id} />;
    case "protractor":
      return <Protractor windowId={win.id} />;
    case "equation":
      return <EquationEditor />;
    case "todo":
      return <Todo />;
    case "magnifier":
      return <Magnifier windowId={win.id} />;
    case "tts":
      return <TTSReader />;
    case "geogebra":
      return <GeoGebra />;
    case "dictionary":
      return <Dictionary />;
    case "browser":
      return <Browser />;
    case "context7":
      return <Context7 />;
    case "askai":
      return <AskAI />;
    case "setsquare":
      return <SetSquare windowId={win.id} />;
    case "annotator":
      return <Annotator />;
    default:
      return null;
  }
}

export default function WindowLayer() {
  const windows = useWindowStore((s) => s.windows);
  const order = useWindowStore((s) => s.order);
  const orderedWindows = useMemo(
    () => order.map((id) => windows[id]).filter(Boolean) as WindowRecord[],
    [order, windows]
  );

  return (
    <div className="fixed inset-0 z-30 pointer-events-none">
      {orderedWindows.map((win) => (
        <div key={win.id} className="pointer-events-auto">
          <DraggableWindow win={win}>
            <WindowContent win={win} />
          </DraggableWindow>
        </div>
      ))}
    </div>
  );
}
